import { Delete } from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Box, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { DataGridPremium, GridCellParams, GridColDef } from "@mui/x-data-grid-premium";
import {
	Department,
	TimeLabelGroup,
	useGetAllTimeLabelGroupsOfDepartmentLazyQuery,
	useAddLabelGroupsToDepartmentMutation,
	useRemoveLabelGroupsFromDepartmentMutation,
} from "@repo/server-api";
import { useNotification } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import React, { useState, useEffect } from "react";


import ConformationDialog from "./Conformation";
import DialogSelectTimeLabelGroups from "./DialogSelectTimeLabelGroups";

export default function DialogTimeLabelsOfDepartment(
	props: Readonly<{
		department: Department;
		open: boolean;
		setOpen: (value: boolean) => void;
		refreshData: () => void;
		timeLabelGroups: TimeLabelGroup[];
	}>,
) {
	const { t } = useTranslation();

	const [open, setOpen] = useState(false);

	const { showNotification } = useNotification();

	const [timeLabelGroups, setTimeLabelGroups] = useState<TimeLabelGroup[]>([]);
	const [saveClicked, setSaveClicked] = useState<boolean>(false);

	const [getTimeLabelGroups] = useGetAllTimeLabelGroupsOfDepartmentLazyQuery({
		fetchPolicy: "no-cache",
		onCompleted(data) {
			if (data) {
				const tmp: TimeLabelGroup[] =
					data.allTimeLabelGroupsOfDepartment?.nodes?.map((group) => ({
						...(group as unknown as TimeLabelGroup),
					})) ?? [];
				setTimeLabelGroups(tmp);
				setSaveClicked(false);
			}
		},
		onError(error) {
			showNotification(error.message, "error");
			setSaveClicked(false);
		},
	});

	useEffect(() => {
		setOpen(props.open);
		if (props.open && props.department.id) {
			getTimeLabelGroups({
				variables: {
					departmentId: props.department.id,
					first: 100,
				},
			});
		}
		 
	}, [props.department.id, props.open]);

	const [addTimeLabelGroups] = useAddLabelGroupsToDepartmentMutation({
		onCompleted(data) {
			const res = data.addLabelGroupsToDepartment;
			if (res.boolean) {
				showNotification(t("timetracking.timeLabels.success.timeLabelGroupsAdded"), "success");
				props.refreshData();
				props.setOpen(false);
				setSaveClicked(false);
			} else if (res.errors) {
				const errors = res.errors;
				errors.forEach((error) => {
					if ("errors" in error && Array.isArray(error.errors)) {
						if (error.errors?.length === 0) showNotification(error.message, "error");
						else
							error.errors?.forEach((e) => {
								showNotification(e.errorMessage, "error");
							});
					} else {
						showNotification(error.message, "error");
					}
				});
				setSaveClicked(false);
			}
		},
		onError(error) {
			showNotification(error.message, "error");
			setSaveClicked(false);
		},
	});

	const [removeTimeLabelGroups] = useRemoveLabelGroupsFromDepartmentMutation({
		onCompleted(data) {
			if (data.removeLabelGroupsFromDepartment.boolean) {
				showNotification(t("timetracking.timeLabels.success.timeLabelGroupsRemoved"), "success");
				props.refreshData();
				props.setOpen(false);
				setSaveClicked(false);
			} else if (data.removeLabelGroupsFromDepartment.errors) {
				const errors = data.removeLabelGroupsFromDepartment.errors;
				errors.forEach((error) => {
					if ("errors" in error && Array.isArray(error.errors)) {
						if (error.errors?.length === 0) showNotification(error.message, "error");
						else
							error.errors?.forEach((e) => {
								showNotification(e.errorMessage, "error");
							});
					} else {
						showNotification(error.message, "error");
					}
				});
				setSaveClicked(false);
			}
		},
		onError(error) {
			showNotification(error.message, "error");
			setSaveClicked(false);
		},
	});

	const handleClose = () => {
		setOpen(false);
		props.setOpen(false);
		setSaveClicked(false);
	};

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.stopPropagation();
		event.preventDefault();
		setSaveClicked(true);

		const newTimeLabelGroups = timeLabelGroups.filter(
			(e: TimeLabelGroup) => !props.timeLabelGroups.find((c) => c.id === e.id),
		);

		const removedTimeLabelGroups = props.timeLabelGroups.filter(
			(e: TimeLabelGroup) => !timeLabelGroups.find((c) => c.id === e.id),
		);

		if (newTimeLabelGroups.length > 0) {
			addTimeLabelGroups({
				variables: {
					timeLabelGroupsIds: newTimeLabelGroups.map((e) => e.id),
					id: props.department.id,
				},
			});
		}
		if (removedTimeLabelGroups.length > 0) {
			removeTimeLabelGroups({
				variables: {
					timeLabelGroupsIds: removedTimeLabelGroups.map((e) => e.id),
					id: props.department.id,
				},
			});
		}

		handleClose();
	};

	const columns: GridColDef<TimeLabelGroup>[] = [
		{
			field: "name",
			headerName: t("name"),
			width: 250,
			flex: 1,
			renderCell(params: GridCellParams) {
				return params.row.displayName;
			},
		},
		{
			field: "isGlobal",
			headerName: t("timetracking.timeLabels.dialogTimeLabelGroups.isGlobal"),
			width: 150,
			renderCell: (params: GridCellParams) => {
				return params.row.isGlobal ? t("yes") : t("no");
			},
		},
		{
			field: "actions",
			headerName: t("actions"),
			sortable: false,
			width: 100,
			renderCell: (params: GridCellParams) => {
				const [openConformationDialog, setOpenConformationDialog] =
					 
					useState(false);
				return (
					<div
						style={{
							justifyContent: "center",
							alignItems: "center",
							display: "flex",
							padding: "7px 0",
						}}>
						<Button data-testid="delete-button" onClick={() => setOpenConformationDialog(true)}>
							<Delete />
						</Button>
						<ConformationDialog
							openPopup={openConformationDialog}
							setOpenDeleteConformation={setOpenConformationDialog}
							text={t("timetracking.timeLabels.deleteConformationTimeLabelGroup")}
							handleYes={() => {
								if (params.row.id) {
									setTimeLabelGroups((prev) => prev.filter((e) => e.id !== params.row.id));
								}
								setOpenConformationDialog(false);
							}}
						/>
					</div>
				);
			},
		},
	];

	const [openDialogTimeLabelGroups, setOpenDialogTimeLabelGroups] = useState(false);
	const [paginationModel, setPaginationModel] = useState({
		pageSize: 5,
		page: 0,
	});

	const [searchValue, setSearchValue] = useState("");
	const [filteredTimeLabelGroups, setFilteredTimeLabelGroups] = useState<TimeLabelGroup[]>([]);
	const handleSearchChange = (value: string) => {
		setSearchValue(value);
		setFilteredTimeLabelGroups(
			timeLabelGroups.filter((group) => group.name.toLowerCase().includes(value.toLowerCase())),
		);
	};

	useEffect(() => {
		setFilteredTimeLabelGroups(
			timeLabelGroups.filter((group) =>
				group.name.toLowerCase().includes(searchValue.toLowerCase()),
			),
		);
	}, [searchValue, timeLabelGroups]);

	return (
		<Dialog
			open={open}
			PaperProps={{
				component: "form",
				onSubmit: handleSubmit,
			}}
			maxWidth="md"
			fullWidth={true}>
			<DialogTitle>
				{t("timetracking.timeLabels.dialogTimeLabelGroups.title")} {t("common.of")}{" "}
				{props.department.name}
				<Button
					data-testid="add-button"
					onClick={() => {
						setOpenDialogTimeLabelGroups(true);
					}}
					className="blueButton right">
					{t("timetracking.timeLabels.dialogTimeLabelGroups.add")}
				</Button>
				<DialogSelectTimeLabelGroups
					open={openDialogTimeLabelGroups}
					setOpen={setOpenDialogTimeLabelGroups}
					timeLabelGroups={timeLabelGroups}
					setTimeLabelGroups={setTimeLabelGroups}
					contactId={props.department.companyId}
				/>
			</DialogTitle>
			<DialogContent>
				<Box
					sx={{
						height: "auto",
						width: "100%",
						borderBottom: "1px solid lightgrey",
					}}>
					<TextField
						value={searchValue}
						onChange={(e) => handleSearchChange(e.target.value)}
						placeholder={t("dropdown.timeLabelGroupSearch")}
						variant="outlined"
						sx={{ marginRight: 2, flex: 1 }}
						className="w-100"
					/>
					<DataGridPremium
						getRowId={(row: TimeLabelGroup) => row.id}
						pagination
						rows={filteredTimeLabelGroups}
						columns={columns}
						className="table"
						disableRowSelectionOnClick
						pageSizeOptions={[1, 2, 5]}
						paginationModel={paginationModel}
						onPaginationModelChange={setPaginationModel}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button data-testid="close-button" variant="outlined" onClick={handleClose}>
					{t("cancel")}
				</Button>
				<LoadingButton
					loading={saveClicked}
					data-testid="dialog-submit-team-member"
					type="submit"
					variant="contained"
					className={saveClicked ? "" : "blueButton"}>
					{t("save")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
}
